import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./Dashboard.css";
import "./ClientPlatformAnalytics.css";
import CphereHeaderLogo from "../components/CphereHeaderLogo";
import { canonicalReportingScopeMatches } from "../platformAnalytics/reportedSnapshotEvidence";
import {
  isAcceptedReportingTimeZone,
  reportingTimeZoneStatement,
  responseReportingTimeZone,
  sourceCalendarQualification,
} from "../utils/reportingCalendar";
import AnalyticsAskEden from "../components/AnalyticsAskEden";
import {
  AdvertiserIdentityTrigger,
  GovernedAdvertiserSelectorModal,
} from "../components/GovernedAdvertiserSelector";
import useGovernedAdvertiserCatalog from "../hooks/useGovernedAdvertiserCatalog";
import { resolvePlatformBranding } from "../utils/platformBranding";
// The report model is shared with the public report microsite
// (/report/:publicReportToken) so both surfaces derive identical KPIs,
// deltas, windows and platform breakdowns from the same canonical payload.
import {
  PLATFORM_ANALYTICS_DATE_PRESETS,
  PLATFORM_ANALYTICS_METRICS,
  buildCampaignKpiStrip,
  buildDailyDeliveryChartModel,
  buildExplicitWindow,
  buildPlatformAnalyticsModel,
  buildPlatformCardPresentation,
  buildPresetWindow,
  focusPlatformAnalyticsModel,
  formatAnalyticsValue,
  formatRatePercent,
  isIsoDayString,
  summaryDailyByChannel,
} from "../platformAnalytics/platformAnalyticsReportModel";
// Which efficiency rate actually describes a platform: CTV is read on video
// view rate, click-bought inventory on CTR. One shared resolver, so a CTV
// column can never state the meaningless 0.00% CTR again.
import {
  resolvePlatformEfficiencyMetric,
  readPlatformCounts,
} from "../platformAnalytics/platformEfficiencyMetric";
// The governed campaign-scope handoff — ONE implementation shared with Quantum
// — so Campaign Analysis can hand this page the campaign it was reporting on
// instead of leaving it to open on the advertiser's first one.
import {
  PLATFORM_ANALYTICS_CAMPAIGN_SCOPE_PARAM_KEYS,
  readPlatformAnalyticsCampaignScope,
  resolvePlatformAnalyticsCampaignScope,
} from "../platformAnalytics/platformAnalyticsCampaignScope";
import {
  buildCampaignScopeFlightWindow,
  buildCampaignScopeFullDeliveryWindow,
  campaignDeliveryExtensionProvenance,
  campaignFullDeliveryProvenance,
  campaignNoDeliveryState,
  campaignSelectorLabel,
} from "../platformAnalytics/campaignReportingLifecycle";
// The delivery visualization is ONE implementation, shared with Quantum's
// /cross-platform-analytics and the public report microsite. This page owns
// the canonical window totals; it does not own a chart of its own.
import DeliveryChart, { DeliveryChartLegend } from "../platformAnalytics/DeliveryChart";
import { buildWindowTotalsDeliveryChartModel } from "../platformAnalytics/deliveryChartModel";
// The platform contribution card is ONE component, shared with Quantum's
// /cross-platform-analytics rail and the public report microsite.
import PlatformContributionCard from "../platformAnalytics/PlatformContributionCard";
import {
  GOVERNED_INTELLIGENCE_STATES,
  resolveGovernedIntelligenceState,
} from "../intelligence/governedIntelligenceState";

/**
 * Cortex Platform Analytics — authenticated, advertiser-scoped, read-only.
 *
 * Data truthfulness contract: every rendered number comes from the canonical
 * advertiser-scoped reporting endpoint (/api/dashboard/performance-summary,
 * client-sanitized server-side). There is no fixture mode, no sample data,
 * and no query-parameter or storage switch that can substitute synthetic
 * metrics. The ONLY query input honored is the governed date-window handoff
 * (startDate/endDate/origin) — explicit reporting dates that scope which
 * canonical window is requested, never what data exists in it. When
 * canonical evidence is absent the page says so explicitly.
 *
 * Financial presentation is client-safe: billed spend renders as "Spend".
 * Internal net media, margin, pricing provenance, and basis diagnostics are
 * never requested, rendered, or described.
 *
 * The report model itself lives in platformAnalytics/platformAnalyticsReportModel
 * because the public report microsite renders the same numbers from the same
 * canonical payload. Re-exported below so this page stays the single import
 * site for anything that already depended on it.
 */

// eslint-disable-next-line no-restricted-exports
export {
  PLATFORM_ANALYTICS_DATE_PRESETS,
  PLATFORM_ANALYTICS_METRICS,
  buildExplicitWindow,
  buildPlatformAnalyticsModel,
  buildPresetWindow,
  formatAnalyticsValue,
} from "../platformAnalytics/platformAnalyticsReportModel";

const API_BASE_URL = (process.env.REACT_APP_BACKEND_URL || "https://cphere-backend.onrender.com").replace(/\/+$/, "");

function getAuthToken() {
  return (
    localStorage.getItem("clientAuthToken") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    ""
  );
}

/**
 * Governed date-window handoff contract. The only URL inputs this page
 * accepts are explicit reporting dates plus a diagnostic origin marker
 * (e.g. the Dashboard preview carrying its resolved window across). No
 * other query input can alter what renders — there is no fixture or data
 * substitution surface here.
 *
 * The campaign scope (see platformAnalyticsCampaignScope) travels alongside
 * these and is governed the same way: it can only SELECT a campaign this page
 * already fetched for the governed advertiser under its own authorization, so
 * it narrows what renders and can never introduce or substitute data.
 */
export const GOVERNED_WINDOW_PARAM_KEYS = Object.freeze([
  "startDate",
  "endDate",
  "origin",
]);

export const parseGovernedWindowParams = (searchParams) => {
  const read = (key) =>
    String(
      (searchParams && typeof searchParams.get === "function"
        ? searchParams.get(key)
        : "") || ""
    ).trim();
  const startDate = read("startDate");
  const endDate = read("endDate");
  if (!isIsoDayString(startDate) || !isIsoDayString(endDate) || startDate > endDate) {
    return null;
  }
  return { startDate, endDate, origin: read("origin") || "handoff" };
};

/**
 * The approved flight as a selectable reporting window — and the DEFAULT one.
 *
 * A campaign report's natural window is the campaign's flight, exactly as the
 * public report offers it. Resolved from the same campaign records the scope
 * selector lists: the selected campaign's flight when one is chosen, the span
 * of all campaign flights otherwise. Null when no campaign carries flight
 * dates — the signal to fall back to the rolling default rather than invent a
 * window.
 */
export const CAMPAIGN_FLIGHT_PRESET_KEY = "campaign_flight";

/*
 * FULL DELIVERY — everything the campaign actually delivered, including any
 * authorized extension and any reported delivery beyond it. A reporting view
 * offered only when it is strictly wider than the original approved flight;
 * it never implies the delivery inside it was authorized, and CAMPAIGN
 * FLIGHT stays the default.
 */
export const FULL_DELIVERY_PRESET_KEY = "full_delivery";

export const buildCampaignFlightWindow = (campaigns = [], campaignId = "") => {
  return buildCampaignScopeFlightWindow(campaigns, campaignId);
};

export const buildCampaignFullDeliveryWindow = (campaigns = [], campaignId = "") => {
  return buildCampaignScopeFullDeliveryWindow(campaigns, campaignId);
};

/**
 * Deterministic, local EDEN command grammar (no model call): date presets,
 * metric selection, and reset. Mirrors the Quantum page's governed pattern
 * without exposing internal prompts.
 */
export const parseClientEdenCommand = (raw) => {
  const text = String(raw || "").toLowerCase();
  if (!text.trim()) return null;
  if (/\breset\b/.test(text)) return { type: "reset" };
  if (/\bflight\b/.test(text)) {
    return {
      type: "range",
      presetKey: CAMPAIGN_FLIGHT_PRESET_KEY,
      label: "Campaign Flight",
    };
  }
  const preset = PLATFORM_ANALYTICS_DATE_PRESETS.find(
    (entry) =>
      text.includes(entry.label.toLowerCase()) ||
      new RegExp(`last\\s+${entry.days}\\s+day`).test(text)
  );
  if (preset) return { type: "range", presetKey: preset.key, label: preset.label };
  const metric = PLATFORM_ANALYTICS_METRICS.find((entry) =>
    text.includes(entry.label.toLowerCase())
  );
  if (metric) return { type: "metric", metricKey: metric.key, label: metric.label };
  return { type: "unsupported" };
};

export default function ClientPlatformAnalytics() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // The governed window (URL-carried explicit dates) is the single source of
  // truth for an explicit window; preset chips take over when it is absent.
  const governedWindow = useMemo(
    () => parseGovernedWindowParams(searchParams),
    [searchParams]
  );
  const governedStartDate = governedWindow?.startDate || null;
  const governedEndDate = governedWindow?.endDate || null;
  // The campaign a surface handed over (Campaign Analysis → Platform
  // Analytics). Read once per URL, resolved against this page's own campaign
  // records below — never trusted as a scope on its own.
  const carriedCampaignScope = useMemo(
    () => readPlatformAnalyticsCampaignScope(searchParams),
    [searchParams]
  );

  const [presetKey, setPresetKey] = useState(CAMPAIGN_FLIGHT_PRESET_KEY);
  /*
   * Whether the reader has chosen a window on this page. Until they do, the
   * default resolves from the campaign records: the approved flight — or,
   * when reported delivery ran past that flight, the FULL DELIVERY window,
   * so a campaign's post-flight delivery is shown rather than hidden behind
   * a chip. Choosing any preset (or a governed handoff window) ends the
   * default; Reset restores it.
   */
  const [presetChosen, setPresetChosen] = useState(false);
  const choosePreset = (key) => {
    setPresetChosen(true);
    setPresetKey(key);
  };
  const [comparison, setComparison] = useState("prior");
  const [metricKey, setMetricKey] = useState("spend");
  const [focusPlatformKey, setFocusPlatformKey] = useState(null);
  const [hoverPlatformKey, setHoverPlatformKey] = useState(null);
  const [describeOpen, setDescribeOpen] = useState(false);
  const [campaignId, setCampaignId] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  // The flight default cannot resolve until the campaign records answer (or
  // fail) — tracked so the first reporting request is made on the RIGHT
  // window instead of a rolling default that gets replaced a beat later.
  const [campaignsResolved, setCampaignsResolved] = useState(false);
  /*
   * Which carried scope has already been honoured. A handoff applies ONCE, to
   * the advertiser it arrived with — switching advertisers must not re-apply a
   * campaign that belonged to the previous one, which is why this deliberately
   * survives clearAdvertiserScopedPresentationState.
   */
  const [appliedCampaignScope, setAppliedCampaignScope] = useState("");
  const [phase, setPhase] = useState("loading");
  const [summary, setSummary] = useState(null);
  const [priorSummary, setPriorSummary] = useState(null);
  const [edenAnswer, setEdenAnswer] = useState("");
  const edenInputRef = useRef(null);
  const advertiserScopeEpochRef = useRef(0);
  const campaignRequestControllerRef = useRef(null);
  const reportingRequestControllerRef = useRef(null);

  const clearAdvertiserScopedPresentationState = useCallback(() => {
    advertiserScopeEpochRef.current += 1;
    campaignRequestControllerRef.current?.abort();
    reportingRequestControllerRef.current?.abort();
    setCampaignId("");
    setCampaigns([]);
    setCampaignsResolved(false);
    setFocusPlatformKey(null);
    setHoverPlatformKey(null);
    setPhase("loading");
    setSummary(null);
    setPriorSummary(null);
    setEdenAnswer("");
    if (edenInputRef.current) edenInputRef.current.value = "";
  }, []);

  const {
    selectedAdvertiserId,
    visibleAdvertisers,
    currentAdvertiser,
    loading: advertiserCatalogLoading,
    selectorDisabled,
    selectorEmpty,
    modalOpen: advertiserSelectorOpen,
    openSelector: openAdvertiserSelector,
    closeSelector: closeAdvertiserSelector,
    handleAdvertiserSelect,
  } = useGovernedAdvertiserCatalog({
    apiBase: API_BASE_URL,
    role: "client",
    onAdvertiserChange: clearAdvertiserScopedPresentationState,
  });
  const advertiserId = Number(currentAdvertiser?.id);

  // Advertiser switches must never leave stale campaign or reporting state.
  useEffect(() => {
    clearAdvertiserScopedPresentationState();
  }, [advertiserId, clearAdvertiserScopedPresentationState]);

  useEffect(() => {
    if (!Number.isFinite(advertiserId) || advertiserId <= 0) return undefined;
    const controller = new AbortController();
    const scopeEpoch = advertiserScopeEpochRef.current;
    campaignRequestControllerRef.current = controller;
    const token = getAuthToken();
    fetch(
      `${API_BASE_URL}/api/media-campaigns?advertiserId=${advertiserId}&reportingScope=1`,
      {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
        signal: controller.signal,
      }
    )
      .then((res) => (res.ok ? res.json() : null))
      .then((payload) => {
        if (
          controller.signal.aborted ||
          scopeEpoch !== advertiserScopeEpochRef.current
        ) {
          return;
        }
        const list = Array.isArray(payload?.campaigns)
          ? payload.campaigns
          : Array.isArray(payload)
          ? payload
          : [];
        setCampaigns(
          list
            .map((row) => ({
              id: row?.id,
              name: row?.campaign_name || row?.campaignName || `Campaign ${row?.id}`,
              flightStartDate: String(
                row?.flight_start_date || row?.flightStartDate || ""
              ).slice(0, 10),
              flightEndDate: String(
                row?.flight_end_date || row?.flightEndDate || ""
              ).slice(0, 10),
              reportingContract:
                row?.reportingContract || row?.reporting_contract || null,
              /*
               * The three governed date contracts travel together: the
               * approved flight (above), the authorized delivery end, and the
               * FULL DELIVERY window the backend resolves from the latest
               * reported delivery. Without the last two this page could only
               * ever offer the approved flight — and a campaign's post-flight
               * delivery had no window to be shown in.
               */
              authorizedDeliveryEndDate: String(
                row?.authorizedDeliveryEndDate || row?.authorized_delivery_end_date || ""
              ).slice(0, 10),
              deliveryExtended: Boolean(row?.deliveryExtended),
              fullDeliveryWindow:
                row?.fullDeliveryWindow && typeof row.fullDeliveryWindow === "object"
                  ? row.fullDeliveryWindow
                  : null,
            }))
            .filter((row) => Number.isFinite(Number(row.id)))
        );
        setCampaignsResolved(true);
      })
      .catch(() => {
        if (
          !controller.signal.aborted &&
          scopeEpoch === advertiserScopeEpochRef.current
        ) {
          // No campaign records ⇒ no flight window; the rolling default takes
          // over rather than blocking reporting on this lookup.
          setCampaignsResolved(true);
        }
      });
    return () => {
      controller.abort();
      if (campaignRequestControllerRef.current === controller) {
        campaignRequestControllerRef.current = null;
      }
    };
  }, [advertiserId]);

  const carriedCampaignScopeSignature = `${carriedCampaignScope.campaignId}\u0000${carriedCampaignScope.campaignName}`;
  const hasCarriedCampaignScope = Boolean(
    carriedCampaignScope.campaignId || carriedCampaignScope.campaignName
  );

  /*
   * Honour a carried campaign scope against THIS page's records.
   *
   * The handoff names a campaign; the campaign records this page fetched under
   * its own authorization decide whether that name means anything here. A
   * scope naming nothing in the governed advertiser's records resolves to "" —
   * the page's existing "All campaigns" — so an unresolvable handoff shows the
   * honest unscoped view instead of quietly reporting a different campaign.
   */
  useEffect(() => {
    if (!hasCarriedCampaignScope) return;
    if (!campaignsResolved) return;
    if (appliedCampaignScope === carriedCampaignScopeSignature) return;
    setAppliedCampaignScope(carriedCampaignScopeSignature);
    setCampaignId(
      resolvePlatformAnalyticsCampaignScope({
        campaigns,
        campaignId: carriedCampaignScope.campaignId,
        campaignName: carriedCampaignScope.campaignName,
      })
    );
  }, [
    appliedCampaignScope,
    campaigns,
    campaignsResolved,
    carriedCampaignScope,
    carriedCampaignScopeSignature,
    hasCarriedCampaignScope,
  ]);

  /*
   * The scope in the URL is the scope on screen.
   *
   * Reloading the page — or landing on it from a handoff and then changing the
   * selector — must not lose the campaign, so the resolved scope is written
   * back to the same params the handoff arrives on. Written only once the
   * records have answered and any carried scope has been honoured, so this can
   * never erase a handoff before it is read.
   */
  useEffect(() => {
    if (!campaignsResolved) return;
    if (hasCarriedCampaignScope && appliedCampaignScope !== carriedCampaignScopeSignature) {
      return;
    }
    const scoped =
      campaigns.find((campaign) => String(campaign.id) === String(campaignId)) || null;
    const nextCampaignId = scoped ? String(scoped.id) : "";
    const nextCampaignName = scoped ? String(scoped.name || "") : "";
    if (
      carriedCampaignScope.campaignId === nextCampaignId &&
      carriedCampaignScope.campaignName === nextCampaignName
    ) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    PLATFORM_ANALYTICS_CAMPAIGN_SCOPE_PARAM_KEYS.forEach((key) => next.delete(key));
    if (nextCampaignId) {
      next.set(PLATFORM_ANALYTICS_CAMPAIGN_SCOPE_PARAM_KEYS[0], nextCampaignId);
      if (nextCampaignName) {
        next.set(PLATFORM_ANALYTICS_CAMPAIGN_SCOPE_PARAM_KEYS[1], nextCampaignName);
      }
    }
    setSearchParams(next, { replace: true });
  }, [
    appliedCampaignScope,
    campaignId,
    campaigns,
    campaignsResolved,
    carriedCampaignScope,
    carriedCampaignScopeSignature,
    hasCarriedCampaignScope,
    searchParams,
    setSearchParams,
  ]);

  /*
   * The approved flight resolves from the campaign records: the selected
   * campaign's flight under a campaign scope, the span of all flights under
   * "All campaigns". It is the DEFAULT selection and reporting window — the
   * same contract the public report states with its Campaign Flight preset —
   * and the rolling presets remain one click away.
   */
  const flightWindow = useMemo(
    () => buildCampaignFlightWindow(campaigns, campaignId),
    [campaigns, campaignId]
  );
  const flightStartDate = flightWindow?.startDate || null;
  const flightEndDate = flightWindow?.endDate || null;
  /*
   * The FULL DELIVERY window — the actual delivery lifecycle. Offered only
   * when it ends strictly later than the campaign-flight window for the same
   * scope: when the two coincide, the chip would be a second name for the
   * same view.
   */
  const fullDeliveryWindow = useMemo(
    () => buildCampaignFullDeliveryWindow(campaigns, campaignId),
    [campaigns, campaignId]
  );
  const fullDeliveryOffered = Boolean(
    flightWindow &&
      fullDeliveryWindow &&
      fullDeliveryWindow.endDate > flightWindow.endDate
  );
  const fullDeliveryStartDate = fullDeliveryOffered
    ? fullDeliveryWindow.startDate
    : null;
  const fullDeliveryEndDate = fullDeliveryOffered
    ? fullDeliveryWindow.endDate
    : null;
  /*
   * Provenance for a client-authorized Full Delivery extension. Stated only
   * when the scope resolves to exactly one campaign — the selected one, or
   * the single campaign the advertiser carries — because the two-contract
   * sentence (original approved flight vs authorized delivery window) is a
   * statement about ONE campaign's authorization, never a blend.
   */
  const deliveryExtensionProvenance = useMemo(() => {
    const scoped = campaignId
      ? campaigns.filter((campaign) => String(campaign.id) === String(campaignId))
      : campaigns;
    return scoped.length === 1
      ? campaignDeliveryExtensionProvenance(scoped[0])
      : null;
  }, [campaigns, campaignId]);
  /*
   * Provenance for the FULL DELIVERY view — same single-campaign rule. It
   * states the original approved flight and either the authorized extension
   * or the governed admission that no authorization was recorded.
   */
  const fullDeliveryProvenance = useMemo(() => {
    const scoped = campaignId
      ? campaigns.filter((campaign) => String(campaign.id) === String(campaignId))
      : campaigns;
    return scoped.length === 1
      ? campaignFullDeliveryProvenance(scoped[0])
      : null;
  }, [campaigns, campaignId]);
  // Once the campaign records have answered, a flight default with no flight
  // dates to stand on falls back to the rolling 30-day preset — visibly, on
  // the chips, not silently. A FULL DELIVERY selection whose window is no
  // longer offered falls back the same way: to the flight when it exists,
  // else to the rolling preset.
  const activePresetKey =
    !presetChosen &&
    presetKey === CAMPAIGN_FLIGHT_PRESET_KEY &&
    campaignsResolved &&
    fullDeliveryOffered
      ? FULL_DELIVERY_PRESET_KEY
      : presetKey === FULL_DELIVERY_PRESET_KEY && campaignsResolved && !fullDeliveryOffered
      ? flightWindow
        ? CAMPAIGN_FLIGHT_PRESET_KEY
        : "last_30d"
      : presetKey === CAMPAIGN_FLIGHT_PRESET_KEY && campaignsResolved && !flightWindow
      ? "last_30d"
      : presetKey;

  useEffect(() => {
    if (!Number.isFinite(advertiserId) || advertiserId <= 0) {
      setPhase("awaiting_advertiser");
      return undefined;
    }
    // Reporting on the flight default waits for the flight dates themselves:
    // requesting a rolling window first would render one window and then
    // replace it — two windows presented as one page load.
    if (
      !(governedStartDate && governedEndDate) &&
      (presetKey === CAMPAIGN_FLIGHT_PRESET_KEY ||
        presetKey === FULL_DELIVERY_PRESET_KEY) &&
      !campaignsResolved
    ) {
      setPhase("loading");
      return undefined;
    }
    // A carried campaign scope waits for the same reason: reporting the
    // advertiser at large first would render every campaign and then replace
    // it with the one that was handed over — two reports presented as one page
    // load, the first of them wrong.
    if (hasCarriedCampaignScope && appliedCampaignScope !== carriedCampaignScopeSignature) {
      setPhase("loading");
      return undefined;
    }
    const controller = new AbortController();
    const scopeEpoch = advertiserScopeEpochRef.current;
    reportingRequestControllerRef.current = controller;
    const token = getAuthToken();
    const window =
      governedStartDate && governedEndDate
        ? buildExplicitWindow(governedStartDate, governedEndDate)
        : activePresetKey === CAMPAIGN_FLIGHT_PRESET_KEY && flightStartDate && flightEndDate
        ? buildExplicitWindow(flightStartDate, flightEndDate)
        : activePresetKey === FULL_DELIVERY_PRESET_KEY &&
          fullDeliveryStartDate &&
          fullDeliveryEndDate
        ? buildExplicitWindow(fullDeliveryStartDate, fullDeliveryEndDate)
        : buildPresetWindow(activePresetKey);

    const fetchSummary = async (startDate, endDate) => {
      const query = new URLSearchParams({
        advertiserId: String(advertiserId),
        startDate,
        endDate,
      });
      if (campaignId) query.set("campaignId", campaignId);
      const res = await fetch(
        `${API_BASE_URL}/api/dashboard/performance-summary?${query.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
          signal: controller.signal,
        }
      );
      if (res.status === 401 || res.status === 403) {
        const denied = new Error("access_denied");
        denied.code = "access_denied";
        throw denied;
      }
      if (!res.ok) throw new Error(`summary_failed_${res.status}`);
      const payload = await res.json();
      if (payload?.ok === false) throw new Error("summary_unavailable");
      // The published canonical contract binds both summary aliases consumed
      // by the page. A successful HTTP response supplies no scope authority.
      const summaries = [payload?.canonicalReporting, payload?.canonicalSummary]
        .filter(value => value && typeof value === "object");
      for (const reporting of summaries) {
        if (reporting.contract != null && (reporting.contract !== "canonical_reporting_v1" ||
            !canonicalReportingScopeMatches(reporting, {
              advertiserId, campaignId: campaignId || null,
              timeframe: { start: startDate, end: endDate },
            }))) throw new Error("summary_scope_unavailable");
      }
      return payload;
    };

    setPhase("loading");
    (async () => {
      try {
        const current = await fetchSummary(window.startDate, window.endDate);
        if (
          controller.signal.aborted ||
          scopeEpoch !== advertiserScopeEpochRef.current
        ) {
          return;
        }
        let prior = null;
        if (comparison === "prior") {
          try {
            prior = await fetchSummary(window.priorStartDate, window.priorEndDate);
          } catch (err) {
            prior = null;
          }
        }
        // A response that resolved just before this scope was superseded must
        // never replace newer advertiser/window state.
        if (
          controller.signal.aborted ||
          scopeEpoch !== advertiserScopeEpochRef.current
        ) {
          return;
        }
        setSummary(current);
        setPriorSummary(prior);
        setPhase("ready");
      } catch (error) {
        if (
          controller.signal.aborted ||
          scopeEpoch !== advertiserScopeEpochRef.current
        ) {
          return;
        }
        setSummary(null);
        setPriorSummary(null);
        setPhase(error?.code === "access_denied" ? "access_denied" : "error");
      }
    })();
    return () => {
      controller.abort();
      if (reportingRequestControllerRef.current === controller) {
        reportingRequestControllerRef.current = null;
      }
    };
  }, [
    advertiserId,
    appliedCampaignScope,
    carriedCampaignScopeSignature,
    hasCarriedCampaignScope,
    presetKey,
    activePresetKey,
    campaignsResolved,
    flightStartDate,
    flightEndDate,
    fullDeliveryStartDate,
    fullDeliveryEndDate,
    comparison,
    campaignId,
    governedStartDate,
    governedEndDate,
  ]);

  // Releasing the explicit window releases the WINDOW. The campaign scope is a
  // different contract and survives it — dropping a date chip must not silently
  // widen the report back to every campaign.
  const clearGovernedWindow = () => {
    if (!governedWindow) return;
    const next = new URLSearchParams(searchParams);
    GOVERNED_WINDOW_PARAM_KEYS.forEach((key) => next.delete(key));
    setSearchParams(next, { replace: true });
  };

  const applyGovernedWindow = (startDate, endDate, origin) => {
    const next = new URLSearchParams(searchParams);
    next.set("startDate", startDate);
    next.set("endDate", endDate);
    next.set("origin", origin);
    setSearchParams(next, { replace: false });
  };

  const model = useMemo(
    () => buildPlatformAnalyticsModel(summary, priorSummary),
    [summary, priorSummary]
  );
  const selectedCampaign = useMemo(
    () =>
      campaigns.find(
        (campaign) => String(campaign.id) === String(campaignId)
      ) || null,
    [campaignId, campaigns]
  );
  const selectedCampaignNoDelivery = campaignNoDeliveryState(selectedCampaign);

  const intelligence = useMemo(
    () =>
      resolveGovernedIntelligenceState({
        initialized: phase !== "awaiting_advertiser",
        loading: phase === "loading",
        error: phase === "error" ? "load_failed" : null,
        evidenceSources: [
          {
            key: "platforms",
            label: "reporting platforms",
            count: model.platforms.length,
            required: true,
          },
        ],
      }),
    [phase, model.platforms.length]
  );

  const requestedWindow = governedWindow
    ? buildExplicitWindow(governedWindow.startDate, governedWindow.endDate)
    : activePresetKey === CAMPAIGN_FLIGHT_PRESET_KEY && flightWindow
    ? buildExplicitWindow(flightWindow.startDate, flightWindow.endDate)
    : activePresetKey === FULL_DELIVERY_PRESET_KEY && fullDeliveryWindow
    ? buildExplicitWindow(fullDeliveryWindow.startDate, fullDeliveryWindow.endDate)
    : buildPresetWindow(activePresetKey);
  const reportedTimeframe = (summary?.canonicalReporting || summary?.canonicalSummary || summary)?.timeframe;
  const hasReportedWindow = isIsoDayString(reportedTimeframe?.start) && isIsoDayString(reportedTimeframe?.end) &&
    reportedTimeframe.start <= reportedTimeframe.end &&
    (!reportedTimeframe.timeZone || isAcceptedReportingTimeZone(reportedTimeframe.timeZone));
  // Values and chart captions describe the actual returned period. The
  // selector's requested window is separate context; absent response evidence
  // cannot borrow those dates and pretend they describe the measurements.
  const window = hasReportedWindow
    ? buildExplicitWindow(reportedTimeframe.start, reportedTimeframe.end)
    : { startDate: null, endDate: null };
  const reportingWindowLabel = hasReportedWindow
    ? `${window.startDate} → ${window.endDate}` : "Reporting window unavailable";
  // The calendar the response states its days in, and whether its platforms
  // report on their own calendars — both from the same canonical answer.
  const reportingTimeZoneNote = hasReportedWindow
    ? reportingTimeZoneStatement(responseReportingTimeZone(summary)) : null;
  const sourceCalendarNote = hasReportedWindow ? sourceCalendarQualification(summary) : null;
  const requestedWindowDiffers = !hasReportedWindow ||
    requestedWindow.startDate !== window.startDate || requestedWindow.endDate !== window.endDate;
  // The tile "solo" interaction: view is the focused presentation of the
  // model when a platform is selected, and the model itself otherwise.
  const view = focusPlatformAnalyticsModel(model, focusPlatformKey) || model;
  const focusedPlatform = view.focusedPlatform || null;
  /*
   * The strip that describes ONE platform, whichever way the page arrived at
   * one: an explicit drill-down, or a campaign whose reporting scope contains
   * a single platform to begin with.
   *
   * Both resolve through the platform's registry profile in the shared model,
   * so they are the same cards in the same order — and on a one-platform
   * campaign they are the same numbers too, because the campaign aggregate is
   * that platform's row. Selecting the sole platform therefore changes the
   * page's scope indicator and nothing a reader is being asked to judge.
   *
   * Null on a mixed flight, on a campaign with no reportable delivery, and on
   * a sole platform that declares no profile of its own — in every one of
   * those the generic campaign strip below renders exactly as before.
   */
  const platformKpiStrip = view.focusedKpiStrip || view.campaignPlatformKpiStrip || null;
  /*
   * Which metrics the delivery chart may be driven by. Resolved AFTER the
   * focus projection because it depends on it: a strip without a Clicks tile
   * must not leave the chart plotting clicks, with nothing on screen to show
   * for the selection or to change it back with. The selection falls back to
   * Spend rather than being silently kept.
   */
  const selectableMetrics = platformKpiStrip
    ? PLATFORM_ANALYTICS_METRICS.filter((entry) =>
        platformKpiStrip.some((tile) => tile.key === entry.key)
      )
    : PLATFORM_ANALYTICS_METRICS;
  const selectedMetric =
    selectableMetrics.find((entry) => entry.key === metricKey) || selectableMetrics[0];
  /*
   * The campaign strip's eight cards, in canonical order. Built from the
   * UNFOCUSED model on purpose: the qualified-video cards state the campaign's
   * cross-platform video and what it cost, which is a different question from
   * "what did this platform measure" and must not silently become scoped when
   * a platform is soloed. A scope that resolves to ONE platform renders
   * `platformKpiStrip` instead and never reaches this.
   */
  const campaignKpiStrip = useMemo(
    () => buildCampaignKpiStrip({ view, model, selectedMetricKey: selectedMetric?.key }),
    [view, model, selectedMetric]
  );
  const togglePlatformFocus = useCallback((platformKey) => {
    setFocusPlatformKey((current) => (current === platformKey ? null : platformKey));
  }, []);

  /*
   * The chart's two governed states, in order of evidence:
   *
   *   1. The canonical payload carries the daily reporting contract
   *      (`dailyByChannel`) — delivery is drawn BY DAY, dated stacked columns
   *      through the same shared time-series builder Quantum's page renders.
   *      Days are dated evidence only: days nobody reported are absent, never
   *      zero, and the days reconcile with the window totals by construction.
   *
   *   2. No daily series — the window-total state of the canonical model, one
   *      certified value per platform with the platform set on the x-axis.
   *
   * Either way each platform's efficiency reads through the one governed
   * resolver: CTV on video view rate, click-bought inventory on CTR.
   */
  const resolveWindowRate = (row) => {
    const resolved = resolvePlatformEfficiencyMetric({
      platformKey: row?.key,
      ...readPlatformCounts(row),
    });
    return resolved
      ? { label: resolved.label, value: formatRatePercent(resolved.value) }
      : null;
  };
  const deliveryChartModel = useMemo(
    () =>
      buildDailyDeliveryChartModel({
        summary,
        model,
        metric: selectedMetric,
      }) ||
      buildWindowTotalsDeliveryChartModel({
        platforms: model.platforms,
        metric: selectedMetric,
        resolveBranding: resolvePlatformBranding,
        formatValue: formatAnalyticsValue,
        window,
        resolveRate: resolveWindowRate,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [summary, model, selectedMetric, window.startDate, window.endDate]
  );
  const chartIsDaily = deliveryChartModel.columnAxis === "time";
  const dailyDisclosure = chartIsDaily ? summaryDailyByChannel(summary) : null;

  const handleEdenSubmit = (event) => {
    event.preventDefault();
    const command = parseClientEdenCommand(edenInputRef.current?.value || "");
    if (!command) return;
    if (command.type === "reset") {
      clearGovernedWindow();
      setPresetChosen(false);
      setPresetKey(CAMPAIGN_FLIGHT_PRESET_KEY);
      setMetricKey("spend");
      setCampaignId("");
      setComparison("prior");
      setEdenAnswer(
        "Filters reset to the default view — the campaign flight window when the campaign carries one."
      );
    } else if (command.type === "range") {
      clearGovernedWindow();
      choosePreset(command.presetKey);
      setEdenAnswer(`Date range set to ${command.label}.`);
    } else if (command.type === "metric") {
      setMetricKey(command.metricKey);
      setEdenAnswer(`Chart metric set to ${command.label}.`);
    } else {
      setEdenAnswer(
        "That request is not supported here yet. Try a date range (\"last 14 days\"), a metric (\"clicks\"), or \"reset\"."
      );
    }
  };

  const ready = phase === "ready" && intelligence.state === GOVERNED_INTELLIGENCE_STATES.READY_FOR_ANALYSIS;

  return (
    <div className="core-dashboard cpx-page-container" data-client-platform-analytics="true">
      <div className="core-dashboard-content cpx-page-content cortex-page-intro-shell">
        <CphereHeaderLogo className="cpx-brand-logo" onClick={() => navigate("/dashboard")} />

        <div className="cphere-governed-hero-selector-stack">
          <div className="cpx-hero-container">
            <div className="cpx-hero-pill" role="heading" aria-level="1" aria-label="Platform Analytics">
              <span className="cpx-hero-pill-label cphere-hero-pill-wordmark">Platform</span>
              <span className="cpx-hero-pill-tag cphere-hero-pill-tag">Analytics</span>
            </div>
            <div className="cpx-hero-shimmer-bar" aria-hidden="true" />
          </div>

          <div className="cphere-governed-advertiser-selector-stage">
            <AdvertiserIdentityTrigger
              advertiser={currentAdvertiser}
              advertiserName={currentAdvertiser?.name || ""}
              apiBase={API_BASE_URL}
              disabled={selectorDisabled}
              loading={advertiserCatalogLoading}
              empty={selectorEmpty}
              modalOpen={advertiserSelectorOpen}
              onClick={openAdvertiserSelector}
            />
          </div>
        </div>

        {advertiserSelectorOpen && visibleAdvertisers.length ? (
          <GovernedAdvertiserSelectorModal
            advertisers={visibleAdvertisers}
            selectedAdvertiserId={selectedAdvertiserId}
            selectedAdvertiserName={currentAdvertiser?.name || ""}
            apiBase={API_BASE_URL}
            onSelect={handleAdvertiserSelect}
            onClose={closeAdvertiserSelector}
          />
        ) : null}

        <div className="cpx-analytics-shell">
          {/*
            The canonical analytics Ask EDEN bar. This page is the design
            source of truth for it, which is precisely why it renders the
            shared component rather than its own copy: a template that is a
            one-off cannot stay a template. The placeholder is this page's to
            choose; nothing else about the control is.
          */}
          <AnalyticsAskEden
            inputRef={edenInputRef}
            onSubmit={handleEdenSubmit}
            placeholder='Ask EDEN — "show clicks for the last 14 days"'
            inputLabel="Ask EDEN about platform performance"
            submitLabel="Submit EDEN request"
            answer={edenAnswer}
            onDismissAnswer={() => setEdenAnswer("")}
          />

          <div className="cpx-filter-row" role="toolbar" aria-label="Analytics filters">
            <select
              className="cpx-select"
              aria-label="Campaign scope"
              value={campaignId}
              onChange={(event) => setCampaignId(event.target.value)}
            >
              <option value="">ALL CAMPAIGNS</option>
              {campaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaignSelectorLabel(campaign)}
                </option>
              ))}
            </select>

            <div className="cpx-chip-group" role="group" aria-label="Date range">
              {governedWindow ? (
                <button
                  type="button"
                  className="cpx-chip cpx-chip--active"
                  aria-pressed="true"
                  data-governed-window-origin={governedWindow.origin}
                  onClick={clearGovernedWindow}
                  title="Explicit reporting window carried from another surface. Select a preset to release it."
                >
                  {governedWindow.startDate} → {governedWindow.endDate}
                </button>
              ) : null}
              {flightWindow ? (
                <button
                  type="button"
                  className={
                    "cpx-chip" +
                    (!governedWindow && activePresetKey === CAMPAIGN_FLIGHT_PRESET_KEY
                      ? " cpx-chip--active"
                      : "")
                  }
                  aria-pressed={
                    !governedWindow && activePresetKey === CAMPAIGN_FLIGHT_PRESET_KEY
                  }
                  onClick={() => {
                    clearGovernedWindow();
                    choosePreset(CAMPAIGN_FLIGHT_PRESET_KEY);
                  }}
                >
                  Campaign Flight
                </button>
              ) : null}
              {fullDeliveryOffered ? (
                <button
                  type="button"
                  className={
                    "cpx-chip" +
                    (!governedWindow && activePresetKey === FULL_DELIVERY_PRESET_KEY
                      ? " cpx-chip--active"
                      : "")
                  }
                  aria-pressed={
                    !governedWindow && activePresetKey === FULL_DELIVERY_PRESET_KEY
                  }
                  onClick={() => {
                    clearGovernedWindow();
                    choosePreset(FULL_DELIVERY_PRESET_KEY);
                  }}
                >
                  Full Delivery
                </button>
              ) : null}
              {PLATFORM_ANALYTICS_DATE_PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  className={
                    "cpx-chip" +
                    (!governedWindow && preset.key === activePresetKey
                      ? " cpx-chip--active"
                      : "")
                  }
                  aria-pressed={!governedWindow && preset.key === activePresetKey}
                  onClick={() => {
                    clearGovernedWindow();
                    choosePreset(preset.key);
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <select
              className="cpx-select"
              aria-label="Comparison period"
              value={comparison}
              onChange={(event) => setComparison(event.target.value)}
            >
              <option value="prior">vs prior period</option>
              <option value="none">No comparison</option>
            </select>

            <button
              type="button"
              className="cpx-chip"
              onClick={() => {
                clearGovernedWindow();
                setPresetChosen(false);
                setPresetKey(CAMPAIGN_FLIGHT_PRESET_KEY);
                setMetricKey("spend");
                setCampaignId("");
                setComparison("prior");
              }}
            >
              Reset
            </button>
          </div>

          {/* The FULL DELIVERY view states its own provenance: the original
              approved flight, then either the authorized extension or the
              governed admission that no authorization was recorded. It never
              implies the delivery inside the window was authorized. */}
          {!governedWindow &&
          activePresetKey === FULL_DELIVERY_PRESET_KEY &&
          fullDeliveryProvenance ? (
            <p
              className="cpx-flight-provenance"
              role="note"
              data-full-delivery="true"
            >
              ORIGINAL APPROVED FLIGHT ·{" "}
              <span className="cpx-numeric">
                {fullDeliveryProvenance.flightStartDate} –{" "}
                {fullDeliveryProvenance.flightEndDate}
              </span>
              {fullDeliveryProvenance.authorizedDeliveryEndDate ? (
                <>
                  {" "}
                  · AUTHORIZED EXTENSION · THROUGH{" "}
                  <span className="cpx-numeric">
                    {fullDeliveryProvenance.authorizedDeliveryEndDate}
                  </span>
                </>
              ) : fullDeliveryProvenance.deliveryBeyondAuthorization ? (
                <> · EXTENSION AUTHORIZATION · NOT RECORDED</>
              ) : null}
            </p>
          ) : /* Two governed date contracts, both visible: the original approved
              flight is history; the authorized delivery window is the current
              execution authority after a Full Delivery extension. */
          deliveryExtensionProvenance ? (
            <p className="cpx-flight-provenance" role="note" data-delivery-extended="true">
              Original approved flight{" "}
              <span className="cpx-numeric">
                {deliveryExtensionProvenance.flightStartDate} →{" "}
                {deliveryExtensionProvenance.flightEndDate}
              </span>{" "}
              · Authorized delivery through{" "}
              <span className="cpx-numeric">
                {deliveryExtensionProvenance.authorizedDeliveryEndDate}
              </span>{" "}
              (Full Delivery)
            </p>
          ) : null}

          {phase === "awaiting_advertiser" ? (
            <div className="cpx-state-panel" role="status" data-analytics-state="awaiting_advertiser">
              <p className="cpx-state-title">No advertiser selected</p>
              <p className="cpx-state-body">
                Select an advertiser to load its platform reporting. Nothing is shown without an
                authorized advertiser scope.
              </p>
            </div>
          ) : null}

          {phase === "access_denied" ? (
            <div className="cpx-state-panel" role="status" data-analytics-state="access_denied">
              <p className="cpx-state-title">Not authorized for this advertiser</p>
              <p className="cpx-state-body">
                Reporting is available only for advertisers connected to your account.
              </p>
            </div>
          ) : null}

          {phase === "error" ? (
            <div className="cpx-state-panel" role="status" data-analytics-state="error">
              <p className="cpx-state-title">Reporting failed to load</p>
              <p className="cpx-state-body">Try again shortly. No values are shown when reporting cannot be verified.</p>
            </div>
          ) : null}

          {phase === "loading" ? (
            <div className="cpx-state-panel" role="status" data-analytics-state="loading">
              <p className="cpx-state-title">Loading platform reporting…</p>
            </div>
          ) : null}

          {phase === "ready" && !model.hasEvidence ? (
            <div
              className="cpx-state-panel"
              role="status"
              data-analytics-state={
                selectedCampaignNoDelivery?.key || "awaiting_platform_data"
              }
            >
              <p className="cpx-state-title">
                {selectedCampaignNoDelivery?.title || "Awaiting platform data"}
              </p>
              <p className="cpx-state-body">
                {selectedCampaignNoDelivery?.message ||
                  "No canonical reporting is available for the selected scope and window yet. Metrics appear here once connected platforms report delivery — nothing is estimated in the meantime."}
              </p>
              {model.latestReportingWindow &&
              (model.latestReportingWindow.start !== window.startDate ||
                model.latestReportingWindow.end !== window.endDate) ? (
                <div className="cpx-state-latest-window">
                  <p className="cpx-state-body">
                    Most recent reporting window for this advertiser:{" "}
                    <span className="cpx-numeric">
                      {model.latestReportingWindow.start} → {model.latestReportingWindow.end}
                    </span>
                    .
                  </p>
                  <button
                    type="button"
                    className="cpx-chip"
                    data-latest-window-cta="true"
                    onClick={() =>
                      applyGovernedWindow(
                        model.latestReportingWindow.start,
                        model.latestReportingWindow.end,
                        "latest_reporting_window"
                      )
                    }
                  >
                    View latest reporting window
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}

          {ready ? (
            <>
              {focusedPlatform ? (
                <div className="cpx-focus-banner" role="status">
                  <span
                    className="cpx-swatch"
                    style={{
                      backgroundColor:
                        resolvePlatformBranding(focusedPlatform.key)?.color ||
                        "var(--cphere-cyan, #7ee7ff)",
                    }}
                    aria-hidden="true"
                  />
                  <span className="cpx-focus-banner-label">
                    Focused on {focusedPlatform.label}
                  </span>
                  <button
                    type="button"
                    className="cpx-chip"
                    onClick={() => setFocusPlatformKey(null)}
                  >
                    Show all platforms
                  </button>
                </div>
              ) : null}

              <p className="cpx-panel-subtitle" data-reporting-period="true">
                Reporting: {reportingWindowLabel}
                {requestedWindowDiffers ? <span> · Requested: {requestedWindow.startDate} → {requestedWindow.endDate}</span> : null}
                {reportingTimeZoneNote ? <span data-reporting-time-zone="true"> · {reportingTimeZoneNote}</span> : null}
              </p>
              {sourceCalendarNote ? (
                <p className="cpx-panel-subtitle" data-source-calendar-qualification="true">{sourceCalendarNote}</p>
              ) : null}
              <div className="cpx-kpi-strip" role="group" aria-label="Summary metrics">
                {platformKpiStrip ? (
                  /*
                   * The sole platform's OWN primary KPIs, resolved from its
                   * registry profile — the same cards, in the same order, with
                   * the same labels and formatting as its dedicated analytics
                   * page. Rendered for an explicit drill-down and for a
                   * campaign that only ever had one platform to aggregate.
                   * Spend and Impressions stay selectable because the delivery
                   * chart plots them; the rest are readings, not columns.
                   */
                  platformKpiStrip.map((tile) => {
                    const selectable = selectableMetrics.some((entry) => entry.key === tile.key);
                    const body = (
                      <>
                        <span className="cpx-kpi-label">{tile.label}</span>
                        <span className="cpx-kpi-value cpx-numeric">{tile.display}</span>
                        {tile.note ? <span className="cpx-kpi-delta">{tile.note}</span> : null}
                      </>
                    );
                    return selectable ? (
                      <button
                        key={tile.key}
                        type="button"
                        className={
                          "cpx-kpi-card cpx-kpi-card--selectable" +
                          (tile.key === selectedMetric.key ? " cpx-kpi-card--active" : "")
                        }
                        onClick={() => setMetricKey(tile.key)}
                        aria-pressed={tile.key === selectedMetric.key}
                        title={tile.definition || undefined}
                      >
                        {body}
                      </button>
                    ) : (
                      <div key={tile.key} className="cpx-kpi-card" title={tile.definition || undefined}>
                        {body}
                      </div>
                    );
                  })
                ) : (
                  /*
                   * The campaign strip, in its canonical order, from the one
                   * contract both Cortex surfaces render. The order and the
                   * membership are not decided here — this file only turns
                   * eight already-resolved descriptors into eight cards.
                   *
                   * That indirection is the fix, not decoration. When this was
                   * inline JSX, "how many cards does the campaign strip have"
                   * was not written down anywhere, so the qualified-video
                   * migration could replace a two-card pair with one card and
                   * leave a seven-card strip in an eight-column grid with
                   * nothing to fail.
                   */
                  campaignKpiStrip.map((card) => {
                    const body = (
                      <>
                        <span className="cpx-kpi-label">{card.label}</span>
                        <span className="cpx-kpi-value cpx-numeric">{card.value}</span>
                        {card.delta ? (
                          <span className={`cpx-kpi-delta cpx-kpi-delta--${card.deltaDirection}`}>
                            {card.delta}
                          </span>
                        ) : card.detail ? (
                          <span className="cpx-kpi-delta">{card.detail}</span>
                        ) : null}
                      </>
                    );
                    return card.selectable ? (
                      <button
                        key={card.key}
                        type="button"
                        className={
                          "cpx-kpi-card cpx-kpi-card--selectable" +
                          (card.active ? " cpx-kpi-card--active" : "")
                        }
                        onClick={() => setMetricKey(card.key)}
                        aria-pressed={card.active}
                        title={card.tooltip || undefined}
                      >
                        {body}
                      </button>
                    ) : (
                      <div key={card.key} className="cpx-kpi-card" title={card.tooltip || undefined}>
                        {body}
                      </div>
                    );
                  })
                )}
              </div>

              <section className="cpx-panel cpx-panel--hero" aria-label="Delivery and efficiency chart">
                <div className="cpx-panel-header">
                  <div>
                    <h2 className="cpx-panel-title">Delivery and efficiency</h2>
                    <p className="cpx-panel-subtitle">
                      {reportingWindowLabel} ·{" "}
                      {chartIsDaily ? "reported by day per platform" : "window totals per platform"} ·{" "}
                      {selectedMetric.label}
                      {focusedPlatform ? ` · ${focusedPlatform.label} focused` : ""}
                    </p>
                  </div>
                </div>
                <DeliveryChart
                  model={deliveryChartModel}
                  mode="absolute"
                  highlightPlatform={hoverPlatformKey || focusPlatformKey}
                  focusPlatform={focusPlatformKey}
                  onTogglePlatform={togglePlatformFocus}
                />
                {/* What the picture above actually is. The dated state says how
                    many days reported and, when some delivery could only be
                    reported as a window total, that those dollars are not in
                    the columns. The window-total state keeps naming what is
                    absent and why. */}
                <p className="cpx-chart-suppression-note">
                  {chartIsDaily
                    ? `Values are reported by day across ${deliveryChartModel.columns.length} reporting day${
                        deliveryChartModel.columns.length === 1 ? "" : "s"
                      }; days without reported delivery are left out rather than shown as zero.${
                        dailyDisclosure?.complete === false
                          ? " Delivery reported only as a window total is absent from these columns."
                          : ""
                      }`
                    : "Values are canonical window totals per platform. The daily series, the efficiency trend, the plan step and the projection appear in this chart once the daily reporting contract is certified — nothing is interpolated in the meantime."}
                </p>
                {/* The chart's footer: what the marks mean, and the way to
                    read them in words. Grouped so the stylesheet can centre
                    them under the chart as one thing. */}
                <div className="cpx-chart-footer">
                  <DeliveryChartLegend model={deliveryChartModel} mode="absolute" />
                  <button
                    type="button"
                    className="cpx-describe-toggle"
                    aria-expanded={describeOpen}
                    onClick={() => setDescribeOpen((open) => !open)}
                  >
                    Describe this chart in words
                  </button>
                  {describeOpen ? (
                    <p className="cpx-describe-text">{deliveryChartModel.textSummary}</p>
                  ) : null}
                </div>
              </section>

              {/* The canonical Platform Analytics contribution rail — the same
                  component Quantum and the public report render, fed from the
                  same shared model. */}
              <div className="cpx-platform-rail" role="group" aria-label="Platform contribution — select a platform to focus its stats">
                {model.platforms.map((row) => {
                  const soloed = focusPlatformKey === row.key;
                  return (
                    <PlatformContributionCard
                      key={"rail-" + row.key}
                      {...buildPlatformCardPresentation(row)}
                      soloed={soloed}
                      inactive={Boolean(focusPlatformKey) && !soloed}
                      onClick={() => togglePlatformFocus(row.key)}
                      onMouseEnter={() => setHoverPlatformKey(row.key)}
                      onMouseLeave={() => setHoverPlatformKey(null)}
                    >
                      {Array.isArray(row.subChannels) && row.subChannels.length ? (
                        <ul
                          className="cpx-platform-subchannel-list"
                          aria-label={`${row.label} channel breakdown`}
                        >
                          {row.subChannels.map((sub) => (
                            <li className="cpx-platform-subchannel" key={"sub-" + sub.key}>
                              <span className="cpx-platform-subchannel-name">{sub.label}</span>
                              <span className="cpx-platform-subchannel-value cpx-numeric">
                                {sub.spend > 0
                                  ? `$${sub.spend.toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}`
                                  : `${formatAnalyticsValue(sub.impressions)} impr.`}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </PlatformContributionCard>
                  );
                })}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
